import { FeatureFlagsContext } from '@/common/featureFlagsWrapper';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useContext, useEffect, useState } from 'react';
import { FetchService } from '../../common/fetch.service';
import { IUnderwriterWithSpecialization, SpecializationsData } from './types';

interface EditSpecializationModalProps {
  open: boolean;
  onClose: () => void;
  underwriter: IUnderwriterWithSpecialization;
  specializations?: SpecializationsData; // Make optional to handle initial undefined state
  isLoadingSpecializations: boolean;
}

export const EditSpecializationModal: React.FC<
  EditSpecializationModalProps
> = ({
  open,
  onClose,
  underwriter,
  specializations,
  isLoadingSpecializations,
}) => {
  const queryClient = useQueryClient();
  const { flags } = useContext(FeatureFlagsContext);
  const isMaxCoverageEditEnabled = flags.isSpecializationMaxCoverageEditEnabled === true;
  const isAuthorityLimitEditEnabled = flags.isSpecializationAuthorityLimitEditEnabled === true;
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [selectedLifecycles, setSelectedLifecycles] = useState<string[]>([]);
  const [selectedMaxCoverage, setSelectedMaxCoverage] = useState<string>('');
  const [selectedAuthorityLimit, setSelectedAuthorityLimit] = useState<string>('');
  const [selectedSpecializationFlags, setSelectedSpecializationFlags] =
    useState<string[]>([]);

  useEffect(() => {
    if (specializations) {
      setSelectedProducts(
        underwriter.specializations
          .filter(
            s =>
              specializations.product.some(
                p => p.name === s.specialization.name,
              ) || false,
          )
          .map(s => s.specialization.name),
      );
      setSelectedLifecycles(
        underwriter.specializations
          .filter(
            s =>
              specializations.lifecycle.some(
                l => l.name === s.specialization.name,
              ) || false,
          )
          .map(s => s.specialization.name),
      );
      const maxCoverage = underwriter.specializations.find(
        s =>
          s.specialization.type === 'MAX_COVERAGE' &&
          specializations.maxCoverage.some(m => m.name === s.specialization.name),
      );
      setSelectedMaxCoverage(
        maxCoverage ? maxCoverage.specialization.name : '',
      );
      const authorityLimitSpec = underwriter.specializations.find(
        s =>
          s.specialization.type === 'AUTHORITY_LIMIT' &&
          specializations.authorityLimit?.some(
            m => m.name === s.specialization.name,
          ),
      );
      setSelectedAuthorityLimit(
        authorityLimitSpec ? authorityLimitSpec.specialization.name : '',
      );
      setSelectedSpecializationFlags(
        underwriter.specializations
          .filter(
            s =>
              specializations.specialization.some(
                f => f.name === s.specialization.name,
              ) || false,
          )
          .map(s => s.specialization.name),
      );
    }
  }, [specializations, underwriter.specializations]);

  const { mutate: updateSpecializations, isLoading: isUpdating } = useMutation({
    mutationFn: (specializationsPayload: { type: string; name: string }[]) =>
      FetchService.getInstance().put(
        `/users/${underwriter.id}/specializations`,
        {
          body: {
            specializations: specializationsPayload,
          },
        },
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['underwriters-specializations'],
      });
      onClose();
    },
    onError: error => {
      console.error('Error updating specializations:', error);
    },
  });

  const handleSubmit = () => {
    // For each coverage type: if the flag is on, use the edited value from state;
    // if off, pass through the existing UW value unchanged (PUT is replace-all).
    const maxCoveragePayload = isMaxCoverageEditEnabled
      ? selectedMaxCoverage
        ? [{ type: 'MAX_COVERAGE', name: selectedMaxCoverage }]
        : []
      : underwriter.specializations
          .filter(s => s.specialization.type === 'MAX_COVERAGE')
          .map(s => ({ type: s.specialization.type, name: s.specialization.name }));

    const authorityLimitPayload = isAuthorityLimitEditEnabled
      ? selectedAuthorityLimit
        ? [{ type: 'AUTHORITY_LIMIT', name: selectedAuthorityLimit }]
        : []
      : underwriter.specializations
          .filter(s => s.specialization.type === 'AUTHORITY_LIMIT')
          .map(s => ({ type: s.specialization.type, name: s.specialization.name }));

    updateSpecializations([
      ...selectedProducts.map(name => ({ type: 'PRODUCT', name })),
      ...selectedLifecycles.map(name => ({ type: 'APPLICATION_LIFECYCLE', name })),
      ...maxCoveragePayload,
      ...authorityLimitPayload,
      ...selectedSpecializationFlags.map(name => ({ type: 'SPECIALIZATION_FLAG', name })),
    ]);
  };

  const renderModalContent = () => {
    if (isLoadingSpecializations || !specializations) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 300,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Stack spacing={3}>
        <FormControl fullWidth>
          <InputLabel>Products</InputLabel>
          <Select
            multiple
            value={selectedProducts}
            label="Products"
            onChange={(e: SelectChangeEvent<string[]>) => {
              setSelectedProducts(
                typeof e.target.value === 'string'
                  ? e.target.value.split(',')
                  : e.target.value,
              );
            }}
            renderValue={selected => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map(value => {
                  const spec = specializations.product.find(
                    s => s.name === value,
                  );
                  return (
                    <Chip
                      key={value}
                      label={spec?.displayText}
                      size="small"
                      sx={{ borderRadius: 1.5 }}
                    />
                  );
                })}
              </Box>
            )}
          >
            {specializations.product.map(spec => (
              <MenuItem key={spec.id} value={spec.name}>
                {spec.displayText}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel>Lifecycles</InputLabel>
          <Select
            multiple
            value={selectedLifecycles}
            label="Lifecycles"
            onChange={(e: SelectChangeEvent<string[]>) => {
              setSelectedLifecycles(
                typeof e.target.value === 'string'
                  ? e.target.value.split(',')
                  : e.target.value,
              );
            }}
            renderValue={selected => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map(value => {
                  const spec = specializations.lifecycle.find(
                    s => s.name === value,
                  );
                  return (
                    <Chip
                      key={value}
                      label={spec?.displayText}
                      size="small"
                      sx={{ borderRadius: 1.5 }}
                    />
                  );
                })}
              </Box>
            )}
          >
            {specializations.lifecycle.map(spec => (
              <MenuItem key={spec.id} value={spec.name}>
                {spec.displayText}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {isMaxCoverageEditEnabled && (
          <FormControl fullWidth>
            <InputLabel>Max Coverage</InputLabel>
            <Select
              value={selectedMaxCoverage}
              label="Max Coverage"
              onChange={(e: SelectChangeEvent) =>
                setSelectedMaxCoverage(e.target.value)
              }
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {specializations.maxCoverage.map(spec => (
                <MenuItem key={spec.id} value={spec.name}>
                  {spec.displayText}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {isAuthorityLimitEditEnabled && (
          <FormControl fullWidth>
            <InputLabel>Authority Limit</InputLabel>
            <Select
              value={selectedAuthorityLimit}
              label="Authority Limit"
              onChange={(e: SelectChangeEvent) =>
                setSelectedAuthorityLimit(e.target.value)
              }
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {specializations.authorityLimit?.map(spec => (
                <MenuItem key={spec.id} value={spec.name}>
                  {spec.displayText}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {specializations.specialization?.length > 0 && (
          <FormControl fullWidth>
            <InputLabel>Specialization Flags</InputLabel>
            <Select
              multiple
              value={selectedSpecializationFlags}
              label="Specialization Flags"
              onChange={(e: SelectChangeEvent<string[]>) => {
                setSelectedSpecializationFlags(
                  typeof e.target.value === 'string'
                    ? e.target.value.split(',')
                    : e.target.value,
                );
              }}
              renderValue={selected => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map(value => {
                    const spec = specializations.specialization.find(
                      s => s.name === value,
                    );
                    return (
                      <Chip
                        key={value}
                        label={spec?.displayText}
                        size="small"
                        sx={{ borderRadius: 1.5 }}
                      />
                    );
                  })}
                </Box>
              )}
            >
              {specializations.specialization.map(spec => (
                <MenuItem key={spec.id} value={spec.name}>
                  {spec.displayText}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Box
          sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}
        >
          <Button variant="outlined" onClick={onClose} disabled={isUpdating}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isLoadingSpecializations || isUpdating}
          >
            {isUpdating ? <CircularProgress size={24} /> : 'Save Changes'}
          </Button>
        </Box>
      </Stack>
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="edit-specializations-modal"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 550,
          bgcolor: 'background.paper',
          borderRadius: 2,
          p: 4,
        }}
      >
        <Typography variant="h6" sx={{ mb: 3 }}>
          Edit Specializations for{' '}
          {underwriter.displayName ||
            `${underwriter.firstName ?? ''} ${underwriter.lastName ?? ''}`.trim() ||
            underwriter.email}
        </Typography>
        {renderModalContent()}
      </Box>
    </Modal>
  );
};
